import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiltersButton, InfoBanner, ReactSelect, TableHeader } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReviewsTable from "./ReviewsTable";
import { getReviewTypeOptions, getSortOptions, ratingOptions } from "@me-team/host/src/utils/utils";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../services/ErrorService";
import { Filter, getInitialValueFilter } from "./constants";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { useGetEmployeesForReviewsQuery } from "@me-team/host/main/reviews/graphql/reviews.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useGetBranchByIdQuery } from "@me-team/host/main/setUpAccessRole/graphql/setUpAccessRole.hooks";
import { useParams } from "react-router-dom";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface Option {
    value: any;
    label: string;
}

const Reviews: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();
    const canViewReviewsAll = useRoleAccess(CompanyAccess.ReviewListView, { scope: PermissionScope.All });
    const canViewReviewsSelf = useRoleAccess(CompanyAccess.ReviewListView, { scope: PermissionScope.Self });
    const canCreateReviewAll = useRoleAccess(CompanyAccess.ReviewCreate, { scope: PermissionScope.All });
    const canCreateReviewSelf = useRoleAccess(CompanyAccess.ReviewCreate, { scope: PermissionScope.Self });
    const canManageCompanyEmployees = useRoleAccess(CompanyAccess.AccessManageCompanyEmployees);
    const isRoleAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const shouldShowOnlySelfReviews = !isRoleAccessPending && !canViewReviewsAll && canViewReviewsSelf;
    const canCreateReview = canCreateReviewAll || canCreateReviewSelf;
    const reviewTypeOptions = getReviewTypeOptions(t);
    const sortOptions = getSortOptions(t);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);

    const { data: currentBranch, loading: currentBranchLoading } = useGetBranchByIdQuery({
        variables: { branchId: branchId, online: false },
        context: { errorType: "local" },
        onCompleted: (data) => {
            const currentBranch = data.branch.id;
            setFilter((prev) => ({
                ...prev,
                branchId: currentBranch,
            }));
        },
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const currentBranchOption: Option = useMemo<Option | null>(() => {
        if (!currentBranch?.branch) return null;

        return {
            value: currentBranch.branch.id,
            label: currentBranch.branch.name,
        };
    }, [currentBranch]);

    useEffect(() => {
        if (currentBranchOption) {
            setSelectedBranch(currentBranchOption);
        }
    }, [currentBranchOption]);

    const branchOptions = [currentBranchOption, { value: -1, label: t("All branches") }];

    const initialValueFilter = getInitialValueFilter(currentBranch?.branch?.id);
    const [selectedBranch, setSelectedBranch] = useState<Option>(null);

    const { data: employeesData, loading } = useGetEmployeesForReviewsQuery({
        variables: {
            id: shouldShowOnlySelfReviews ? Number(currentBranchEmployeeId) : null,
            itemsPerPage: 1000,
            filterByNameAndPosition: true,
            email: null,
            companyId: +currentCompanyId,
            branchId: selectedBranch?.value === -1 ? null : selectedBranch?.value,
        },
        skip: !currentCompanyId || (shouldShowOnlySelfReviews && !currentBranchEmployeeId),
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const employeesOptions = useMemo(
        () =>
            employeesData?.user?.company?.employees?.employees?.map((employee) => {
                return {
                    value: employee.id,
                    label: `${employee.name} ${employee.surname} (${employee.position.name})`,
                };
            }) || [],
        [employeesData]
    );
    const selfEmployeeOption: Option | null = useMemo(() => {
        if (!shouldShowOnlySelfReviews || !currentBranchEmployeeId) return null;

        return employeesOptions.find((option) => option.value === Number(currentBranchEmployeeId)) ?? null;
    }, [shouldShowOnlySelfReviews, currentBranchEmployeeId, employeesOptions]);

    const [showCreateModal, setShowCreateModal] = useState(false);

    const [selectedReviewType, setSelectedReviewType] = useState(null);
    const [selectedRating, setSelectedRating] = useState(null);
    const [selectedReviewEmployees, setSelectedReviewEmployees] = useState(null);
    const [selectedSort, setSelectedSort] = useState({
        label: t("New first"),
        value: ["createdAt", "DESC"],
    });

    useEffect(() => {
        setSelectedSort({
            label: t("New first"),
            value: ["createdAt", "DESC"],
        });
    }, [i18n.language, t]);

    const [showFilters, setShowFilters] = useState(false);
    const [filter, setFilter] = useState<Filter>(initialValueFilter);

    const handleCreateReview = () => {
        setShowCreateModal(true);
    };

    const toggleFilters = () => setShowFilters(!showFilters);

    const handleReviewTypeChange = (selectedOption: Option) => {
        setSelectedReviewType(selectedOption);
    };

    const handleRatingChange = (selectedOption: Option) => {
        setSelectedRating(selectedOption);
    };

    const handleEmployeesChange = (selectedOption: Option) => {
        setSelectedReviewEmployees(selectedOption);
    };

    const handleSortChange = (selectedOption: Option) => {
        setSelectedSort(selectedOption);
    };

    const handleApplyFilter = () => {
        setFilter({
            employeeId: selectedReviewEmployees?.value ? selectedReviewEmployees?.value : null,
            reviewType: selectedReviewType?.value ? selectedReviewType?.value : null,
            rating: selectedRating?.value ? selectedRating?.value : null,
            reviewSortField: selectedSort?.value && selectedSort?.value[0],
            order: selectedSort?.value && selectedSort?.value[1],
            branchId: selectedBranch?.value && selectedBranch?.value,
        });
    };

    const handleClearFilter = () => {
        setFilter({
            employeeId: null,
            reviewType: null,
            rating: null,
            reviewSortField: "createdAt",
            order: "DESC",
            branchId: currentBranchOption?.value,
        });
        setSelectedReviewType(null);
        setSelectedRating(null);
        setSelectedReviewEmployees(null);
        setSelectedSort({ label: t("New first"), value: ["createdAt", "DESC"] });
        setSelectedBranch(currentBranchOption);
    };

    if (isRoleAccessPending || currentBranchLoading || loading) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">{t("You can view all reviews on this page")}</p>
            </InfoBanner>

            <TableHeader
                title={t("List of reviews")}
                paths={[
                    {
                        path: `/booking/review/company/${currentCompanyId}/${branchId}`,
                        label: t("Personnel management"),
                    },
                    {
                        path: `/booking/review/company/${currentCompanyId}/${branchId}`,
                        label: t("List of reviews"),
                    },
                ]}
                colSize={8}
                btnColSize={4}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        {canCreateReview ? (
                            <Col xs={6} lg={7} xxl={6}>
                                <Button variant="primary" className="w-100" onClick={handleCreateReview}>
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {t("Add Review")}
                                </Button>
                            </Col>
                        ) : null}
                        <Col xs={6} lg={5} xxl={4}>
                            <FiltersButton filter={filter} showFilters={showFilters} onClick={toggleFilters} />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <Row className="gx-3">
                    <Col xl={9}>
                        <Row className="gx-3 h-100">
                            <Col
                                sm={!shouldShowOnlySelfReviews ? 12 : 6}
                                lg={canManageCompanyEmployees ? 2 : 3}
                                className="text-primary mb-3 mb-xl-0"
                            >
                                <ReactSelect
                                    name="employeeId"
                                    id="reviews-employeeId-field"
                                    value={shouldShowOnlySelfReviews ? selfEmployeeOption : selectedReviewEmployees}
                                    options={employeesOptions}
                                    placeholder={t("Review modal and table.Employee")}
                                    onChange={handleEmployeesChange}
                                    isSearchable={true}
                                    disabled={shouldShowOnlySelfReviews}
                                />
                            </Col>
                            <Col sm={6} lg={3} className="text-primary mb-3 mb-xl-0">
                                <ReactSelect
                                    name="reviewType"
                                    id="reviews-reviewType-field"
                                    value={selectedReviewType}
                                    options={reviewTypeOptions}
                                    placeholder={t("Review Type")}
                                    onChange={handleReviewTypeChange}
                                    isSearchable={false}
                                />
                            </Col>
                            <Col sm={6} lg={canManageCompanyEmployees ? 2 : 3} className="text-primary mb-3 mb-xl-0">
                                <ReactSelect
                                    name="reviewSortField"
                                    id="reviews-reviewSortField-field"
                                    value={selectedRating}
                                    options={ratingOptions}
                                    placeholder={t("Review modal and table.Rating")}
                                    onChange={handleRatingChange}
                                    isSearchable={false}
                                />
                            </Col>
                            <Col sm={6} lg={3} className="text-primary mb-3 mb-xl-0">
                                <ReactSelect
                                    name="order"
                                    id="reviews-order-field"
                                    value={selectedSort}
                                    options={sortOptions}
                                    placeholder={t("Sorting")}
                                    onChange={handleSortChange}
                                    isSearchable={false}
                                />
                            </Col>
                            {canManageCompanyEmployees ? (
                                <Col sm={6} lg={2} className="mb-3 mb-xl-0">
                                    <ReactSelect
                                        id="rewiews-branchId-field"
                                        placeholder={t("Branch")}
                                        options={branchOptions}
                                        value={branchOptions.find((option) => option.value === selectedBranch?.value)}
                                        onChange={(selectedOption: Option) => setSelectedBranch(selectedOption)}
                                        isSearchable={false}
                                    />
                                </Col>
                            ) : null}
                        </Row>
                    </Col>
                    <Col lg={6} xl={3} className="mt-2 mt-sm-0 ms-lg-auto">
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark border-grey w-100 h-100  fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </button>
                            </Col>
                            <Col xs={6}>
                                <button className="btn btn-primary w-100 h-100 px-1" onClick={handleApplyFilter}>
                                    {t("apply")}
                                </button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>
            <ReviewsTable
                filter={filter}
                setShowCreateModal={setShowCreateModal}
                showCreateModal={showCreateModal}
                canCreateReview={canCreateReview}
                canCreateReviewAll={canCreateReviewAll}
                canViewReviewsAll={canViewReviewsAll}
                canViewReviewsSelf={canViewReviewsSelf}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default Reviews;
